import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    TextField, Box, Typography, Snackbar, Alert, FormControlLabel, 
    Checkbox, Grid, Divider, Select, MenuItem, FormControl 
} from '@mui/material';
import * as API from '../../api';

const CustomLabel = ({ title, required = false }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title} {required && <span style={{ color: 'red' }}>*</span>}
    </Typography>
);

const HumanResourceDialog = ({ open, onClose, initialData, onSuccess }) => {
    const [formData, setFormData] = useState({
        id: null,
        username: '',
        password: '',
        first_name: '',
        last_name: '',
        email: '',
        is_employee: false,
        phone: '',
        job_title: '',
        bank_info: '', // Thêm trường ngân hàng cho nhân viên
        is_shareholder: false,
        profit_percentage: 0,
        debt_percentage: 0
    });

    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const showToast = (msg, severity = 'success') => setToast({ open: true, message: msg, severity });
    const handleCloseToast = () => setToast({ ...toast, open: false });

    useEffect(() => {
        if (open) {
            if (initialData) {
                // Map dữ liệu khi sửa
                setFormData({
                    ...initialData,
                    password: '' // Không hiện mật khẩu cũ
                });
            } else {
                // Reset form khi thêm mới
                setFormData({
                    id: null,
                    username: '',
                    password: '',
                    first_name: '',
                    last_name: '',
                    email: '',
                    is_employee: false,
                    phone: '',
                    job_title: '',
                    bank_info: '',
                    is_shareholder: false,
                    profit_percentage: 0,
                    debt_percentage: 0
                });
            }
        }
    }, [open, initialData]);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async () => {
        if (!formData.username) {
            showToast("Vui lòng nhập Username", "error");
            return;
        }

        try {
            // Gọi API save_hr_profile đã tạo ở backend
            await API.client.post('/human-resources/save_hr_profile/', formData);
            showToast(initialData ? "Cập nhật thành công" : "Thêm mới thành công", "success");
            setTimeout(() => onSuccess(), 1000);
        } catch (error) {
            const msg = error.response?.data?.error || error.message;
            showToast("Có lỗi xảy ra: " + msg, "error");
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#007373', color: 'white', fontWeight: 'bold' }}>
                    {initialData ? 'CẬP NHẬT NHÂN SỰ' : 'THÊM MỚI NHÂN SỰ'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Username (Tên đăng nhập)" required />
                                <TextField 
                                    name="username" size="small" fullWidth 
                                    value={formData.username} onChange={handleChange} 
                                    disabled={!!formData.id} 
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title={formData.id ? "Mật khẩu (Để trống nếu không đổi)" : "Mật khẩu"} required={!formData.id} />
                                <TextField 
                                    name="password" type="password" size="small" fullWidth 
                                    value={formData.password} onChange={handleChange} 
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Họ (Last Name)" />
                                <TextField name="last_name" size="small" fullWidth value={formData.last_name} onChange={handleChange} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Tên (First Name)" required />
                                <TextField name="first_name" size="small" fullWidth value={formData.first_name} onChange={handleChange} />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Email" />
                                <TextField name="email" size="small" fullWidth value={formData.email} onChange={handleChange} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <CustomLabel title="Quyền truy cập" />
                                <Select
                                    name="system_role"
                                    value={formData.system_role}
                                    onChange={handleChange}
                                    sx={{ bgcolor: 'white' }}
                                >
                                    <MenuItem value="SALE">Quyền Sale (Chỉ nhập/sửa form)</MenuItem>
                                    <MenuItem value="OPERATOR">Quyền Điều hành (Form + Quản lý xe/tour)</MenuItem>
                                    <MenuItem value="ACCOUNTANT">Quyền Kế toán (Chỉ xem tab kế toán quản trị)</MenuItem>
                                    <MenuItem value="MANAGER">Quyền Quản lý / Cổ đông (Toàn quyền)</MenuItem>
                                </Select>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Divider sx={{ my: 1 }} />

                        <Box sx={{ display: 'flex', gap: 4 }}>
                            
                            <Box sx={{ flex: 1 }}>
                                <FormControlLabel 
                                    control={<Checkbox checked={formData.is_employee} onChange={handleChange} name="is_employee" color="primary" />} 
                                    label={<Typography variant="subtitle2" fontWeight="bold" color="primary">Nhân viên</Typography>} 
                                />
                                {formData.is_employee && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                        <Box>
                                            <CustomLabel title="Số điện thoại" />
                                            <TextField name="phone" size="small" fullWidth value={formData.phone} onChange={handleChange} />
                                        </Box>
                                        <Box>
                                            <CustomLabel title="Chức vụ / Vị trí" />
                                            <TextField name="job_title" size="small" fullWidth value={formData.job_title} onChange={handleChange} />
                                        </Box>
                                        <Box>
                                            <CustomLabel title="Thông tin Ngân hàng" />
                                            <TextField 
                                                name="bank_info" size="small" fullWidth multiline rows={2} 
                                                value={formData.bank_info} onChange={handleChange} 
                                                placeholder="VD: VCB - 123456 - Nguyen Van A" 
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            
                            <Divider orientation="vertical" flexItem />

                            <Box sx={{ flex: 1 }}>
                                <FormControlLabel 
                                    control={<Checkbox checked={formData.is_shareholder} onChange={handleChange} name="is_shareholder" color="secondary" />} 
                                    label={<Typography variant="subtitle2" fontWeight="bold" color="secondary">Cổ đông</Typography>} 
                                />
                                {formData.is_shareholder && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                                        <Box>
                                            <CustomLabel title="% Chia Lợi Nhuận" />
                                            <TextField name="profit_percentage" type="number" size="small" fullWidth value={formData.profit_percentage} onChange={handleChange} />
                                        </Box>
                                        <Box>
                                            <CustomLabel title="% Chia Công Nợ/Góp vốn" />
                                            <TextField name="debt_percentage" type="number" size="small" fullWidth value={formData.debt_percentage} onChange={handleChange} />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} variant="outlined" color="inherit">Hủy</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#007373' }}>Lưu dữ liệu</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={toast.open} autoHideDuration={2000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">{toast.message}</Alert>
            </Snackbar>
        </>
    );
};

export default HumanResourceDialog;